import { GAME, MAP } from '../utils/constants';
import { BUILDING_LOT_PAD, isParkLot } from '../game/mapLayout';
import { getRampMinimapPoints } from '../game/ramps';

/** Same scale as `drawMinimap` — used for drag deltas in GameView. */
export function getMinimapScale(logicalW: number, logicalH: number): number {
  return Math.min(logicalW, logicalH) / (MAP.SIZE * 1.06);
}

/**
 * Heading-up minimap: `mapHeading` matches the chase car so forward is always toward the top of the widget
 * (same feel as GTA). Center = player + world pan; drag deltas use current heading in GameView. Lateral
 * screen X is D·right with right = (cos θ, −sin θ). Player arrow uses `mapHeading` (usually points up).
 */
export function drawMinimap(
  ctx: CanvasRenderingContext2D,
  logicalW: number,
  logicalH: number,
  centerPx: number,
  centerPz: number,
  mapHeading: number,
  player: { x: number; z: number; heading: number },
  cops?: { x: number; z: number }[],
  /** Single-player rounds move the goal; omit to use `GAME.EXTRACTION_*`. */
  extraction?: { x: number; z: number },
  /** Road props — cyan dots; pursuit breakers as orange squares (see `destructibles`). */
  destructibleDots?: { x: number; z: number; breaker: boolean }[],
): void {
  const HALF = MAP.HALF;
  const scale = getMinimapScale(logicalW, logicalH);
  const { ROAD_WIDTH: rw, SIDEWALK_WIDTH: sw, GRID: G } = MAP;
  const roadWithSidewalk = rw + sw * 2;
  const totalRoadSpace = (G + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / G;
  const span = blockW + roadWithSidewalk;

  const toMini = (wx: number, wz: number): { x: number; y: number } => {
    const dx = wx - centerPx;
    const dz = wz - centerPz;
    const sin = Math.sin(mapHeading);
    const cos = Math.cos(mapHeading);
    const lat = dx * cos - dz * sin;
    const fwd = dx * sin + dz * cos;
    return {
      x: logicalW / 2 + lat * scale,
      y: logicalH / 2 - fwd * scale,
    };
  };

  ctx.save();
  ctx.clearRect(0, 0, logicalW, logicalH);
  ctx.fillStyle = '#0c1018';
  ctx.fillRect(0, 0, logicalW, logicalH);

  ctx.strokeStyle = 'rgba(255,255,255,0.14)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, logicalW - 1, logicalH - 1);

  /** Block interior setback hint (non–park): full cell, then tower drawn on top. */
  ctx.fillStyle = 'rgba(45, 78, 52, 0.14)';
  for (let iz = 0; iz < G; iz++) {
    for (let ix = 0; ix < G; ix++) {
      if (isParkLot(ix, iz)) continue;
      const xmin = -HALF + roadWithSidewalk + ix * span;
      const xmax = xmin + blockW;
      const zmin = -HALF + roadWithSidewalk + iz * span;
      const zmax = zmin + blockW;
      const a = toMini(xmin, zmin);
      const b = toMini(xmax, zmin);
      const c = toMini(xmax, zmax);
      const d = toMini(xmin, zmax);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(d.x, d.y);
      ctx.closePath();
      ctx.fill();
    }
  }

  /** Tower footprint (building mass) — not drivable through. */
  ctx.fillStyle = 'rgba(22, 24, 32, 0.72)';
  for (let iz = 0; iz < G; iz++) {
    for (let ix = 0; ix < G; ix++) {
      if (isParkLot(ix, iz)) continue;
      const xmin = -HALF + roadWithSidewalk + ix * span;
      const xmax = xmin + blockW;
      const zmin = -HALF + roadWithSidewalk + iz * span;
      const zmax = zmin + blockW;
      const halfW = (blockW - BUILDING_LOT_PAD) * 0.5;
      const cx = (xmin + xmax) * 0.5;
      const cz = (zmin + zmax) * 0.5;
      const tminX = cx - halfW;
      const tmaxX = cx + halfW;
      const tminZ = cz - halfW;
      const tmaxZ = cz + halfW;
      const a = toMini(tminX, tminZ);
      const b = toMini(tmaxX, tminZ);
      const c = toMini(tmaxX, tmaxZ);
      const d = toMini(tminX, tmaxZ);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(d.x, d.y);
      ctx.closePath();
      ctx.fill();
    }
  }

  /** Soft world edge (matches collision margin ~5 m + car radius). */
  const edgeInset = 12;
  const ex0 = -HALF + edgeInset;
  const ex1 = HALF - edgeInset;
  const ez0 = -HALF + edgeInset;
  const ez1 = HALF - edgeInset;
  const eA = toMini(ex0, ez0);
  const eB = toMini(ex1, ez0);
  const eC = toMini(ex1, ez1);
  const eD = toMini(ex0, ez1);
  ctx.setLineDash([5, 6]);
  ctx.strokeStyle = 'rgba(255, 160, 80, 0.42)';
  ctx.lineWidth = 1.25;
  ctx.beginPath();
  ctx.moveTo(eA.x, eA.y);
  ctx.lineTo(eB.x, eB.y);
  ctx.lineTo(eC.x, eC.y);
  ctx.lineTo(eD.x, eD.y);
  ctx.closePath();
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  ctx.lineWidth = 1.2;
  for (let i = 0; i <= G; i++) {
    const z = -HALF + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    const a = toMini(-HALF, z);
    const b = toMini(HALF, z);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
  for (let j = 0; j <= G; j++) {
    const x = -HALF + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    const a = toMini(x, -HALF);
    const b = toMini(x, HALF);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }

  /** Open park lots — full cell drivable; stroke = “park” vs setback grass. */
  for (let iz = 0; iz < G; iz++) {
    for (let ix = 0; ix < G; ix++) {
      if (!isParkLot(ix, iz)) continue;
      const xmin = -HALF + roadWithSidewalk + ix * span;
      const xmax = xmin + blockW;
      const zmin = -HALF + roadWithSidewalk + iz * span;
      const zmax = zmin + blockW;
      const a = toMini(xmin, zmin);
      const b = toMini(xmax, zmin);
      const c = toMini(xmax, zmax);
      const d = toMini(xmin, zmax);
      ctx.fillStyle = 'rgba(0, 210, 95, 0.32)';
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(d.x, d.y);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(0, 255, 150, 0.75)';
      ctx.lineWidth = 1.6;
      ctx.stroke();
    }
  }

  const pulse = 0.65 + Math.sin(performance.now() * 0.004) * 0.35;
  const exW = extraction?.x ?? GAME.EXTRACTION_X;
  const exZ = extraction?.z ?? GAME.EXTRACTION_Z;
  const ex = toMini(exW, exZ);
  ctx.fillStyle = `rgba(0, 255, 160, ${0.35 + pulse * 0.25})`;
  ctx.beginPath();
  ctx.arc(ex.x, ex.y, 10 * scale * 4.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(0, 255, 200, 0.9)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.fillStyle = '#ff9a33';
  for (const r of getRampMinimapPoints()) {
    const p = toMini(r.x, r.z);
    const s = 7 * scale * 3.2;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y - s);
    ctx.lineTo(p.x - s * 0.88, p.y + s * 0.62);
    ctx.lineTo(p.x + s * 0.88, p.y + s * 0.62);
    ctx.closePath();
    ctx.fill();
  }

  if (destructibleDots && destructibleDots.length > 0) {
    for (const d of destructibleDots) {
      const p = toMini(d.x, d.z);
      if (d.breaker) {
        ctx.fillStyle = 'rgba(255, 130, 55, 0.95)';
        ctx.strokeStyle = 'rgba(255, 220, 200, 0.9)';
        ctx.lineWidth = 1.1;
        const w = 5;
        ctx.fillRect(p.x - w / 2, p.y - w / 2, w, w);
        ctx.strokeRect(p.x - w / 2, p.y - w / 2, w, w);
      } else {
        ctx.fillStyle = 'rgba(0, 230, 255, 0.72)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  if (cops) {
    for (let i = 0; i < cops.length; i++) {
      const c = toMini(cops[i].x, cops[i].z);
      ctx.fillStyle = i === 0 ? '#ff3355' : '#ff6688';
      ctx.strokeStyle = 'rgba(255,255,255,0.75)';
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.arc(c.x, c.y, 5.5 * scale * 3.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }

  const p = toMini(player.x, player.z);
  const ang = player.heading - mapHeading;
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(ang);
  ctx.fillStyle = '#00e8ff';
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.lineWidth = 1.25;
  ctx.beginPath();
  ctx.moveTo(0, -9);
  ctx.lineTo(-7, 7);
  ctx.lineTo(7, 7);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  ctx.restore();
}
